import React, { useEffect, useState, useRef, useCallback } from "react";
import { InitiatePaymentParams } from "../../api/interfaces/payments";
import { CheckPaymentStatus, InitiatePublicPayment } from "../../api/payments";
import { useRequest } from "../../api/utils";
import { InvoiceInterface, INVOICE_STATUSES } from "../../assets/interfaces/invoices";
import { PAYMENT_METHODS } from "../../assets/interfaces/payments";
import useSteps from "../hooks/useSteps";
import Portal from "../portal";
import PaystackPayment from "./paystack-payment";
import ZillaPayment from "./zilla-payment";
import BankTransfers from "./bank-transfers";
import SelectPaymentMethod from "./select-payment-method";
import PaymentSuccessful from "./payment-successful";
import PaymentFailed from "./payment-failed";
import PaymentAwaitingConfirmation from "./awaiting-confirmation";
import PaymentMade from "./payment-made";
import NoPaymentInformation from "./no-payment";
import { toast } from "../ui/toast";
import { emit } from "../hooks/useListener";
import MonoDirectDebit from "./directpay-payment";
import { OrderInterface } from "../../assets/interfaces";
import ThepeerPayment from "./thepeer-payment";
import MoMoPayment from "./momo-payment";
import { useAppendScript } from "../hooks/useAppendScript";
import LeatherbackPayment from "./leatherback-payment";
import { useModals } from "../hooks/useModals";

interface IProps {
  invoiceData: {
    preview_amount: number;
    invoice: string;
    initial_status: INVOICE_STATUSES;
    storeName: string;
    storePhone: string;
    receipt?: string;
    currency: string;
  };
  orderId?: string;
  onPaymentSuccess?: VoidFunction;
  payFor?: InvoiceInterface["receiver"];
  byCustomer?: boolean;
  chowbotPhone?: string;
  order?: OrderInterface;
  paymentEnabled: boolean;
}

enum PAYMENT_STEPS {
  SELECT_METHOD = "SELECT_METHOD",
  MAKE_BANK_TRANSFER = "MAKE_BANK_TRANSFER",
  MAKE_MOMO_PAYMENT = "MAKE_MOMO_PAYMENT",
  WAITING_FOR_CONFIRMATION = "WAITING_FOR_CONFIRMATION",
  PAYMENT_SUCCESSFUL = "PAYMENT_SUCCESSFUL",
  PAYMENT_FAILED = "PAYMENT_FAILED",
  NO_PAYMENT_INFORMATION = "NO_PAYMENT_INFORMATION",
  PAYMENT_MADE = "PAYMENT_MADE",
}

const MakePayment: React.FC<IProps> = (props) => {
  const {
    invoiceData,
    orderId,
    onPaymentSuccess,
    payFor = null,
    byCustomer = false,
    order,
    chowbotPhone,
    paymentEnabled,
  } = props;
  const type = orderId ? "ORDER" : "INVOICE";
  const initiatePaymentRequest = useRequest<InitiatePaymentParams>(InitiatePublicPayment);
  const checkPaymentStatusRequest = useRequest(CheckPaymentStatus);
  const [paymentMethod, setPaymentMethod] = useState<PAYMENT_METHODS>(null);
  const { step, changeStep } = useSteps(
    [
      PAYMENT_STEPS.SELECT_METHOD,
      PAYMENT_STEPS.MAKE_BANK_TRANSFER,
      PAYMENT_STEPS.WAITING_FOR_CONFIRMATION,
      PAYMENT_STEPS.PAYMENT_SUCCESSFUL,
      PAYMENT_STEPS.PAYMENT_FAILED,
      PAYMENT_STEPS.NO_PAYMENT_INFORMATION,
      PAYMENT_STEPS.PAYMENT_MADE,
    ],
    0
  );
  const { modals, toggleModal } = useModals(["iframe"]);
  const paymentData = initiatePaymentRequest?.response?.data;
  const [statusUnknowm, setStatusUnknowm] = useState(false);
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);
  const [receiptId, setReceiptId] = useState(invoiceData?.receipt);
  const [waitFor, setWaitFor] = useState(3); //delay until payment is marked as failed
  const [sseActive, setSseActive] = useState(true);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useAppendScript(
    "https://leatherbackstorage.blob.core.windows.net/frontend/Aaas/cdn/leatherbackpopup.js",
    "LEATHERBACK_JS"
  );
  useAppendScript("https://js.paystack.co/v1/inline.js", "PAYSTACK_JS");

  useEffect(() => {
    if (!paymentEnabled) {
      changeStep(PAYMENT_STEPS.NO_PAYMENT_INFORMATION);
    }
  }, [paymentEnabled]);

  // Helper function to check payment status
  const checkPaymentStatus = useCallback(async () => {
    if (!paymentData?.reference) return false;

    try {
      const [res, err] = await checkPaymentStatusRequest.makeRequest({ reference: paymentData.reference });

      if (res && res.data?.status === "SUCCESS") {
        setPaymentSuccessful(true);
        setReceiptId(res.data?.receipt);
        onPaymentSuccess && onPaymentSuccess();
        changeStep(PAYMENT_STEPS.PAYMENT_SUCCESSFUL);

        // Clear interval after successful payment
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        return true;
      }
    } catch (error) {
      console.error("Polling error:", error);
    }
    return false;
  }, [paymentData?.reference, onPaymentSuccess, changeStep, checkPaymentStatusRequest]);

  // Helper to start polling
  const startPolling = useCallback(() => {
    // Clean up existing interval if it exists
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Only start polling if we have the conditions
    if (
      document.visibilityState === "visible" &&
      !sseActive &&
      (step === PAYMENT_STEPS.WAITING_FOR_CONFIRMATION ||
        step === PAYMENT_STEPS.MAKE_BANK_TRANSFER ||
        step === PAYMENT_STEPS.MAKE_MOMO_PAYMENT) &&
      paymentData?.reference &&
      !paymentSuccessful
    ) {
      pollIntervalRef.current = setInterval(() => {
        // Only check when tab is visible
        if (document.visibilityState === "visible") {
          checkPaymentStatus();
        }
      }, 10000); // Poll every 10 seconds
    }
  }, [sseActive, step, paymentData?.reference, paymentSuccessful, checkPaymentStatus]);

  // Helper to stop polling
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  ///for payment widget on storefront
  useEffect(() => {
    if (step === PAYMENT_STEPS.SELECT_METHOD) {
      emit("payment-not-started", true);
    } else {
      emit("payment-not-started", false);
    }
  }, [step]);

  useEffect(() => {
    if (!invoiceData.invoice) {
      changeStep(PAYMENT_STEPS.NO_PAYMENT_INFORMATION);
      return;
    }

    if (invoiceData.initial_status === INVOICE_STATUSES.PAID) {
      changeStep(PAYMENT_STEPS.PAYMENT_MADE);
      return;
    }

    const clientId = `PAYMENTS.${invoiceData.invoice}`;
    const evtSource = new EventSource(`${process.env.NEXT_PUBLIC_API_URL}/events?client_id=${clientId}`);

    evtSource.onmessage = (e) => {
      setSseActive(true);
      const data = JSON.parse(e.data);

      if (data.event === "PAYMENT_SUCCESSFUL" && data?.payload?.invoice === invoiceData.invoice) {
        setPaymentSuccessful(true);
        setReceiptId(data?.payload?.receipt);
        onPaymentSuccess && onPaymentSuccess();
        changeStep(PAYMENT_STEPS.PAYMENT_SUCCESSFUL);
      }
    };

    evtSource.onerror = (e) => {
      console.error("SSE error", e);
      const now = Date.now();

      // If we haven't recorded an error in the last second (avoid duplicate errors)
      setSseActive(false);
    };

    return () => {
      evtSource.close();
      stopPolling();
    };
  }, [stopPolling]);

  // Set up polling as fallback when SSE is not active
  useEffect(() => {
    // Clean up and restart if necessary
    stopPolling();
    startPolling();

    return stopPolling;
  }, [sseActive, step, paymentData?.reference, paymentSuccessful, startPolling, stopPolling]);

  // Handle visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        stopPolling();
      } else {
        startPolling();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [startPolling, stopPolling]);

  useEffect(() => {
    const handleWindowClose = (e) => {
      e = e || window.event;
      // window.confirm

      // For IE and Firefox prior to version 4
      if (e) {
        e.returnValue = "Please keep this page open to complete your payment";
      }

      // For Safari
      return "Please keep this page open to complete your payment";
    };

    if (paymentData && !paymentSuccessful && !statusUnknowm) {
      window.addEventListener("beforeunload", handleWindowClose);
    } else {
      window.removeEventListener("beforeunload", handleWindowClose);
    }

    return () => window.removeEventListener("beforeunload", handleWindowClose);
  }, [paymentSuccessful, statusUnknowm, paymentData]);

  const initiatePayment = async (payment_method_type: PAYMENT_METHODS) => {
    setPaymentMethod(payment_method_type);

    const [res, err] = await initiatePaymentRequest.makeRequest({
      type: "INVOICE",
      payment_method_type,
      invoice: invoiceData?.invoice,
      currency: invoiceData?.currency,
    });

    if (res) {
      switch (payment_method_type) {
        case PAYMENT_METHODS.TRANSFER:
          changeStep(PAYMENT_STEPS.MAKE_BANK_TRANSFER);
          break;
        case PAYMENT_METHODS.PAYSTACK:
          break;
        case PAYMENT_METHODS.MOMO:
          changeStep(PAYMENT_STEPS.MAKE_MOMO_PAYMENT);
        case PAYMENT_METHODS.ZILLA:
          //initiate zilla payment
          break;
        case PAYMENT_METHODS.MONO_DIRECT_PAY:
          // initiate mono payment
          break;
        case PAYMENT_METHODS.STARTBUTTON:
          handleRedirectPayments(res);
          break;
        case PAYMENT_METHODS.STRIPE:
          handleRedirectPayments(res);
          break;
        default:
        //do nothing
      }

      return;
    }

    //error for if initiation failed
    toast.error({ title: "Initiation Failed", message: err?.message ?? "Please try another payment method" });
  };

  function onSuccess(reference?: any) {
    if (step === PAYMENT_STEPS.SELECT_METHOD) {
      changeStep(PAYMENT_STEPS.WAITING_FOR_CONFIRMATION);
    }
  }

  function onError() {
    changeStep(PAYMENT_STEPS.PAYMENT_FAILED);
  }

  function handleRedirectPayments(res) {
    changeStep(PAYMENT_STEPS.WAITING_FOR_CONFIRMATION);
    if (res?.data?.payment_link) {
      setWaitFor(30);
      window.open(res?.data?.payment_link, "_blank");
    } else {
      changeStep(PAYMENT_STEPS.SELECT_METHOD);
      toast.error({ title: "Initiation Failed", message: "Please try another payment method" });
    }
  }

  const redirectUrl = initiatePaymentRequest?.response?.data?.payment_link;

  return (
    <>
      {step === PAYMENT_STEPS.SELECT_METHOD && (
        <SelectPaymentMethod
          paymentMethod={paymentMethod}
          invoiceData={invoiceData}
          initiatePayment={initiatePayment}
          initiatePaymentRequest={initiatePaymentRequest}
          payFor={payFor}
          byCustomer={byCustomer}
          // payTo={payTo}
        />
      )}
      {step === PAYMENT_STEPS.PAYMENT_SUCCESSFUL && (
        <PaymentSuccessful
          amount={invoiceData.preview_amount}
          reference={paymentData?.reference}
          storeName={invoiceData.storeName}
          storePhone={invoiceData.storePhone}
          orderId={orderId ?? invoiceData.invoice}
          type={type}
          payFor={payFor}
          chowbotPhone={chowbotPhone}
          receipt={receiptId}
          order={order}
          currency={invoiceData?.currency}
        />
      )}
      {step === PAYMENT_STEPS.PAYMENT_MADE && (
        <PaymentMade
          storePhone={invoiceData.storePhone}
          orderId={orderId ?? invoiceData.invoice}
          type={type}
          payFor={payFor}
          // payTo={payTo}
          receipt={receiptId}
        />
      )}
      {step === PAYMENT_STEPS.NO_PAYMENT_INFORMATION && (
        <NoPaymentInformation
          storePhone={invoiceData.storePhone}
          storeName={invoiceData.storeName}
          orderId={orderId ?? invoiceData.invoice}
        />
      )}
      {step === PAYMENT_STEPS.PAYMENT_FAILED && (
        <PaymentFailed
          amount={invoiceData.preview_amount}
          reference={paymentData?.reference}
          restart={() => changeStep(PAYMENT_STEPS.SELECT_METHOD)}
          unsure={statusUnknowm}
          currency={invoiceData?.currency}
        />
      )}
      {step === PAYMENT_STEPS.WAITING_FOR_CONFIRMATION && (
        <PaymentAwaitingConfirmation
          paymentSuccessful={paymentSuccessful}
          handleSuccess={() => changeStep(PAYMENT_STEPS.PAYMENT_SUCCESSFUL)}
          triggerError={() => {
            setStatusUnknowm(true);
            changeStep(PAYMENT_STEPS.PAYMENT_FAILED);
          }}
          waitFor={waitFor}
          goBack={() => changeStep(PAYMENT_STEPS.SELECT_METHOD)}
          addon={
            redirectUrl ? (
              <div className="text-1xs text-dark mt-3 text-center max-w-[250px]">
                If you didn&apos;t get automatically redirected to a payment page{" "}
                <a href={redirectUrl} target="_blank" rel="noreferrer" className="font-semibold text-primary-500">
                  please click here.
                </a>
              </div>
            ) : null
          }
        />
      )}
      {step === PAYMENT_STEPS.MAKE_BANK_TRANSFER && paymentData && (
        <BankTransfers
          goBack={() => changeStep(PAYMENT_STEPS.SELECT_METHOD)}
          paymentData={{
            amount: paymentData?.total_amount,
            bank: paymentData?.bank,
          }}
          triggerError={() => {
            setStatusUnknowm(true);
            changeStep(PAYMENT_STEPS.PAYMENT_FAILED);
          }}
        />
      )}
      {step === PAYMENT_STEPS.MAKE_MOMO_PAYMENT && (
        <MoMoPayment
          goBack={() => changeStep(PAYMENT_STEPS.SELECT_METHOD)}
          paymentData={{
            invoice: invoiceData?.invoice,
            total_amount: paymentData?.total_amount,
            reference: paymentData?.reference,
            networks: paymentData?.networks,
          }}
          triggerError={() => {
            setStatusUnknowm(true);
            changeStep(PAYMENT_STEPS.PAYMENT_FAILED);
          }}
          invoiceData={invoiceData}
        />
      )}
      <Portal>
        {paymentMethod === PAYMENT_METHODS.PAYSTACK && paymentData !== undefined && (
          <PaystackPayment
            paymentData={{
              email: paymentData?.customer?.email || "holla@catlog.shop",
              amount: paymentData?.total_amount,
              reference: paymentData?.reference,
              publicKey: paymentData?.public_key,
              currency: paymentData?.currency,
            }}
            onClose={onError}
            callback={onSuccess}
          />
        )}

        {paymentMethod === PAYMENT_METHODS.LEATHERBACK && paymentData !== undefined && (
          <LeatherbackPayment
            paymentData={{
              email: paymentData?.customer?.email || "holla@catlog.shop",
              amount: paymentData?.total_amount,
              reference: paymentData?.reference,
              publicKey: paymentData?.public_key,
              currency: paymentData?.currency,
              customerName: paymentData?.customerName ?? "Catlog",
            }}
            onClose={onError}
            callback={onSuccess}
          />
        )}

        {paymentMethod === PAYMENT_METHODS.THEPEER && paymentData !== undefined && (
          <ThepeerPayment
            paymentData={{
              email: paymentData?.customer?.email || "holla@catlog.shop",
              customer: paymentData?.customer?.id,
              amount: paymentData?.total_amount,
              reference: paymentData?.reference,
              publicKey: paymentData?.public_key,
            }}
            onClose={onError}
            onSuccess={onSuccess}
          />
        )}

        {paymentMethod === PAYMENT_METHODS.ZILLA && paymentData !== undefined && (
          <ZillaPayment
            paymentData={{
              orderCode: paymentData?.zilla_order_id,
              amount: paymentData?.total_amount,
              reference: paymentData?.reference,
              publicKey: paymentData?.public_key,
            }}
            onClose={onError}
            onSuccess={onSuccess}
          />
        )}

        {paymentMethod === PAYMENT_METHODS.MONO_DIRECT_PAY && paymentData !== undefined && (
          <MonoDirectDebit
            paymentData={{
              amount: paymentData?.total_amount,
              payment_id: paymentData.mono_payment_id,
              publicKey: paymentData.public_key,
            }}
            onClose={onError}
            callback={onSuccess}
          />
        )}
      </Portal>
    </>
  );
};

export default MakePayment;
